import {StandardPropagator} from "./Propagator.js";
import {create_object} from "../intouch.js";
import {skin} from "../utils/Skin.js";
import {id} from "../utils/Util.js";
import {msg} from "@/i18n/Message.ts";

const ACTION = {
	NONE: -1,				// do nothing
	PROPAGATE_EMPTY: -2,	// select empty
	ADD_RECORD: -3,			// invoke create editor
	PROPAGATE_NEW: -4,		// propagate as new value (modern version of ADD_RECORD)
	PROPAGATE_FIRST: 0		// propagate first found item
};

const EXPAND_QUERY_ATTR = "data-query";
const LIST_SIZE = 20;


export class Suggest {
	/**
	 * @param queryInput	reference to jQuery <input> element providing value
	 * @param resultsDiv	jQuery <div> which will be used as a container for rendering results (may be null)
	 * @param suggester		name of a server suggester (string)
	 * @param queryParser	instance of mapping function that transform string obtained from queryInput to actual query sent to server
	 * @param dataPropagator instance of Propagator class
	 * @param clientOptions	options object with extended configuration
	 * @param serverOptions	string which will be passed to server with every query
	 *
	 */
	constructor(queryInput, resultsDiv, suggester, queryParser, dataPropagator, clientOptions, serverOptions) {
		// Init
		this.widget = queryInput;
		this.results = resultsDiv === null ? this.makeResultsDiv() : resultsDiv;
		this.suggester = suggester;
		this.queryParser = queryParser;
		this.propagator = dataPropagator;
		this.options = $.extend({
			separator: null,
			suggEmpty: true,
			stayFocused: false,
			addEntity: null,	// TODO redefine behavior
			formColor: '000000'
		}, clientOptions);

		this.serverOptions = serverOptions;
        this.extractServerOptions = extractContext(this.serverOptions);

		this.lastQuery = null;
		this.selrow = null;
		this.cache = {};
		this.rows = 0;
		this.data = null;
		this.justSearching = null;
		this.lastMaxSize = 0;  //stored previos value, used to detect change
		this.maxSize = LIST_SIZE;  // initial value

		// Remember current data
		this.originalId = this.propagator.id;
		this.originalTitle = this.propagator.title;
		this.widget.val(this.originalTitle);
		this.originalClass = this.widget.attr('class');

		// Bind
		this.bind();
	}

	makeResultsDiv() {
		const div = $('<div class="results"></div>');
		div.css('display', 'none');
		this.widget.parent().append(div);
		return div;
	}



	bind() {
		this.widget.on('input', this.inputChanged.bind(this));
		this.widget.on('keydown', this.keyDown.bind(this));
		this.widget.on('blur', this.blur.bind(this));
		this.widget.on('focus', this.focus.bind(this));
	}

	focus() {
		this.lastQuery=null;
		if (this.lastMaxSize === 0) {
			this.addSeparator();
            //console.log("add separateor")
		}
        this.extractServerOptions = extractContext(this.serverOptions); //refresh server options from context
		this.triggerSearchFromInputWidget();
	}

	inputChanged() {
		this.widget.removeAttr(EXPAND_QUERY_ATTR);
		this.triggerSearchFromInputWidget();
	}

	triggerSearchFromInputWidget() {
		let word = this.queryWord;
		if ((word === this.lastQuery) && (word !== '' || !this.options.suggEmpty)) return;
		if (word.length < this.options.chars) {
			this.hideSuggestions();
			return;
		}
		this.queryServerWith(word);
	}

	keyDown(e) {
		if (e.keyCode === 38 /* UP */) {
			e.preventDefault();
			if (this.rows > 0) {
				if (this.selrow === null || this.selrow <= 0) this.selectRow(this.rows-1);	// select last
				else this.selectRow(this.selrow-1);
			}
		} else if (e.keyCode === 39 /* RIGHT */ && this.data && this.data.items) {
			if (this.selrow >= 0 && this.selrow < this.data.items.length) {
				let item = this.data.items[this.selrow];
				if (item && item.expand) {
					e.preventDefault();
					this.widget.attr(EXPAND_QUERY_ATTR, item.expand);
					this.queryServerWith(item.expand);
				}
			}
		} else if (e.keyCode === 40 /* DOWN */) {
			e.preventDefault();
			if (this.rows > 0) {
				if (this.selrow === null || this.selrow >= (this.rows-1)) this.selectRow(0);// select first
				else this.selectRow(this.selrow+1);
			}
		} else if (e.keyCode === 13 /* ENTER */) {
			e.preventDefault();
			this.doAction();
		} else if (e.keyCode === 27 /* ESC */) {
			e.preventDefault();
			this.widget.blur();
			this.widget.val(this.originalTitle);
			this.propagator.id = this.originalId;
			this.propagator.title = this.originalTitle;
		}
	}

	selectRow(rowNum) {
		if (rowNum === this.selrow) return;	// already selected
		this.unselectRow();
		let nr = this.results.find('tr[data-row="'+rowNum+'"]');
		if (nr.length > 0) {
			nr.addClass("selected");
			this.selrow = rowNum;
		}
	}

	/** Remove selection from current row */
	unselectRow() {
		if (this.selrow === null) return;	// none selected
		var or = this.results.find('tr[data-row="'+this.selrow+'"]');
		if (or.length > 0) {
			or.removeClass("selected");
			this.selrow = null;
		}
	}

	haveData() {
		return this.data && this.data.items && this.data.items.length > 0;
	}

	isRowSelected() {
		return this.selrow !== null;
	}

	wordIsEmpty() {
		return this.queryWord.length === 0;
	}

	canSuggestEmptyValue() {
		return this.options.suggEmpty;
	}

	canSuggestNewValue() {
		return this.suggNew;
	}

	isOnFirstItem() {
		if (this.options.separator) {
			return this.widget.val().lastIndexOf(this.options.separator) < 0;
		} else return true;
	}

	doExpand() {
		this.lastMaxSize = this.maxSize;
		this.maxSize = this.maxSize+LIST_SIZE;
        //console.log("Do expand:"+this.lastMaxSize+" ,maxSize:"+this.maxSize);
		this.widget.focus();
	}

	doAction() {

		if (this.data!==null && this.selrow === this.data.items.length) {
			// Extra row with "Add new"  was picked
			this.widget.blur();
			create_object(this.options.addEntity, this.propagator, this.lastQuery);
		}
		let propagateRow = ACTION.NONE;	// >=0 propagate row

		// Important! this is a decision tree
		if (this.haveData()) {
			if (this.isRowSelected()) {
				propagateRow = this.selrow;
			} else {
				if (this.wordIsEmpty()) {
					if (this.canSuggestEmptyValue()) propagateRow = ACTION.PROPAGATE_EMPTY;
					else propagateRow = ACTION.PROPAGATE_FIRST;
				} else {
					if (this.canSuggestNewValue()) propagateRow = ACTION.PROPAGATE_NEW;
					else propagateRow = ACTION.PROPAGATE_FIRST;
				}
			}
		} else {
			if (this.wordIsEmpty()) {
				if (this.canSuggestEmptyValue()) propagateRow = ACTION.PROPAGATE_EMPTY;
				else propagateRow = ACTION.NONE;
			} else {
				if (this.canSuggestNewValue()) propagateRow = ACTION.PROPAGATE_NEW;
				else if (this.canSuggestEmptyValue()) propagateRow = ACTION.PROPAGATE_EMPTY;
				else propagateRow = ACTION.NONE;
			}
		}

		if (propagateRow === ACTION.NONE) {
			return;
		} else if (propagateRow === ACTION.PROPAGATE_EMPTY) {
			// empty
			if (this.isOnFirstItem()) {
				this.propagator.id = "0";
				this.widget.val(this.propagator.title = "");
				this.propagator.propagate({ id:{} });
				this.hideSuggestions();
				this.widget.blur();
			}
			return;
		} else if (propagateRow === ACTION.PROPAGATE_NEW) {
			if (this.isOnFirstItem()) {
				this.propagator.id = "0";
				this.hideSuggestions();
				if (typeof this.onnew === 'function') this.onnew(this.widget.val());
				else this.widget.blur();
			}
			return;
		} else if (propagateRow === ACTION.ADD_RECORD) {
			console.log("ADD RECORD not implemented");
			return;
		}
		// ACTION.ROW

		if (propagateRow >= 0 && propagateRow < this.data.items.length) {
			// Propagate data row
			const data = this.data.items[propagateRow];
			let dataValue = "";
			// Store primary key to pk-hidden-input
			if (typeof data.id === "string") dataValue = data.id;
			else dataValue = data.id.main;

			if (dataValue==="...") {
                this.doExpand();
				return;
			}

			if (this.options.separator) {
				const idx = this.propagator.id.lastIndexOf(this.options.separator);
				if (idx > 0) {
					dataValue = this.propagator.id.substr(0, idx).concat(this.options.separator, ' ', dataValue);
				}
			}
			this.propagator.id = dataValue;

			if (this.options.diffTitle) { // Do the same for title - skip if title=pk - not needed
				let theTitle;
				if (typeof data.title === "string") theTitle = data.title;
				else theTitle = data.title.main;

				if (this.options.separator && this.options.separator.length === 1) {
					let q = this.widget.val();
					const idx = q.lastIndexOf(this.options.separator);
					if (idx <= 0) {
						this.widget.val(theTitle);
						this.propagator.title = theTitle;
					} else {
						let curr = q.substr(0, idx);
						const v = curr.concat(this.options.separator, ' ', theTitle);
						this.widget.val(v);
						this.propagator.title = v;
					}
				} else {
					this.widget.val(theTitle);
					this.propagator.title = theTitle;
				}
			}

			this.originalTitle = this.widget.val();	// for future escape
			this.originalId = this.propagator.id;
			this.propagator.propagate(data);

			// put query field to the original state
			this.widget.attr("class", this.originalClass);
			this.hideSuggestions();

			if (this.options.stayFocused) {
				this.addSeparator();
			} else {
				if (!(this.options.separator && this.options.separator.length === 1)) this.widget.blur();	// stay only in if multivalue
			}

            if (this.lastMaxSize === this.maxSize) {
                this.lastMaxSize = 0;
                this.maxSize=LIST_SIZE;
            }
		}
	}

	addSeparator() {
		const temp = this.widget.val().trim();
		if (this.options.separator && temp.length>0 && !temp.endsWith(this.options.separator)) {
			this.widget.val(temp+this.options.separator+' ');
			let input = this.widget.get(0);
			input.selectionStart = input.selectionEnd = input.value.length;
		}
	}

	blur() {
		if (this.canSuggestEmptyValue() && this.wordIsEmpty()) {
			if (this.isOnFirstItem()) {
				this.propagator.id = "0";
				this.widget.val(this.propagator.title = "");
				this.propagator.propagate({ id:{} });
			}
		}

		setTimeout(function () {
			if (!this.widget.is(":focus")) {
				if (this.options.separator && this.options.separator.length === 1 && this.widget.val().trim().endsWith(this.options.separator)) {
					let temp = this.widget.val().trim();
					this.widget.val(this.propagator.title = temp.substring(0, temp.length - 1));
				}
				this.hideSuggestions();
			}
		}.bind(this), 200);
		return true;
	}

	hideSuggestions() {
		this.results.hide();
		this.rows = 0;
		this.selrow = null;
		this.cache = {};	// invalidate fetched data
		this.data = null;
		this.lastQuery = null;
	}

	queryServerWith(query) {
		//console.log("Q = " + query);
		if (typeof query === 'undefined' || query === null) query = '';
		if (this.lastQuery !== null && this.lastQuery === query) {
			return;
		} // called twice
		if (this.justSearching !== null) {
			// Search in progress
			if (query !== this.justSearching)  this.searchInQueue = query;
			return;
		}
		this.justSearching = query;
		this.lastQuery = query;
		const hashKey = this.hashKey(query + this.extractServerOptions);
		const cachedData = this.cache[hashKey];

		if (typeof cachedData !== 'undefined' && cachedData !== null && this.lastMaxSize===this.maxSize) {	// serve from cache
			this.searchDone(cachedData);
			this.searchFinally(cachedData);
			return;
		}
		const data = {};
		data.id = this.widget.attr('id');
		if (data.id === null || typeof data.id === 'undefined') data.id = '_unknown_';
		data.query = query;
		data.suggester = this.suggester;
		data.options = this.extractServerOptions+",maxSize="+this.maxSize;
		this.lastMaxSize=this.maxSize; //this value sending to server, so now its actual value

		jQuery.ajax('suggestfeed.do', { data: data, type: 'POST' }).done(function(result) {
			this.cache[hashKey] = result;	// remember
			this.searchDone(result);
		}.bind(this))
			.always(this.searchFinally.bind(this))
			.fail(function(status, ex) { console.log('SUGGEST ERROR '+status+' '+ex); });
	}

	hashKey(str) {
		if (str === null || typeof str === 'undefined' || str.length === 0) return "___empty___";
		return str.replace('.', '_').replace(/\s/g, '_');
	}

	/**
	 * This is called just as client receives new data from server
	 */
	searchDone(result) {
		this.displaySuggestions(result);
	}

	/**
	 * The format is
	 * { instruction: {...}, items: [...] }
	 */
	displaySuggestions(result) {
		let resultsDiv = this.results;
		resultsDiv.empty();
		this.selrow = null;
		this.rows = 0;		// incremented in loop bellow
		this.data = result;	// remember current data
		const ins = result.instructions;

		let emptyRows = true;
		let hasMore = ins.moreSuggests;

		const t = $('<table class="suggResult"></table>');
		let span = 1;

		const rowEnter = function(rowIdx) { this.selectRow(rowIdx); };
		const rowLeave = function(rowIdx) { this.unselectRow(); };
		const rowClick = function(rowIdx) { this.doAction(); };

		/* Check if there is an expansion query */
		let expansion = false;
		for (let i = 0; i < result.items.length; i++) {
			const item = result.items[i];
			if (typeof item.expand === 'string') {
				expansion = true;
				break;
			}
		}

		for (let i = 0; i < result.items.length; i++)
		{
			const item = result.items[i];
			const rowStyle = i%2===0 ? "evenrow" : "oddrow";
			const r = $('<tr data-row="'+i+'" class="'+rowStyle+'"></tr>');
			t.append(r);
			// Emit columns
			let curSpan = 0;
			for (let j = 1; j <= 7; j++) {
				if (item.hasOwnProperty('c'+j)) {
					const d = $('<td></td>');
					if (ins.columnClasses) {
						const cc = ins.columnClasses[j-1];
						if (cc && cc.length>0) d.addClass(cc);
					}
					let ct = 'text';
					if (ins.columnTypes) {
						const ctt = ins.columnTypes[j-1];
						if (ctt) ct = ctt;
					}
					if (ct === 'html') {
						d.html(item['c'+j]);
					} else if (ct === 'icon') {
						const i = skin.iconTag(item['c'+j], resultsDiv);
						d.css("width", "26px").css("text-align", "center");
						d.append(i);
					} else if (ct.indexOf('img ')===0) {
						const parts = ct.split(' ');
						const i = $('<img>');
						i.attr('src', item['c'+j]);
						i.attr('width', parts[1]);
						i.attr('height', parts[2]);
						d.append(i);
					} else {
						d.text(item['c'+j]);
					}
					r.append(d);
					curSpan++;
				} else break;
			}
			if (expansion) {
				const d = $('<td></td>');
				if (typeof item.expand === 'string') {
					const expIcn = $(Icon.fi("boxed-arrow-right", "link-like-fi"));
					expIcn.attr(EXPAND_QUERY_ATTR, item.expand);
					d.append(expIcn);
					d.css("text-align", "right");
					const that = this;
					expIcn.on('click', function(e) {
						e.stopPropagation();
						let eq = $(this).attr(EXPAND_QUERY_ATTR);
						that.widget.attr(EXPAND_QUERY_ATTR, eq);
						that.widget.focus();
					});
				}
				r.append(d);
				curSpan++;
			}
			if (curSpan > span) {}span = curSpan;
			this.rows++;
			emptyRows=false;
			// Hook row
			r.on('mouseenter', rowEnter.bind(this, i));
			r.on('mouseleave', rowLeave.bind(this, i));
			r.on('click', rowClick.bind(this, i));
		}

		if (!hasMore && this.options.addEntity!==null && this.options.addEntity.length>0) {
			let idx = this.rows++;
			const rowStyle = idx%2===0 ? "evenrow" : "oddrow";
			const r = $('<tr data-row="' + idx + '" class="' + rowStyle + '"></tr>');
			const d = $('<td></td>');
			if (span > 1) d.attr("colspan", span);
			d.append(skin.iconTag("plus-circle-18-full"));
			d.append('<span style="font-weight: bold;"> '+msg("menu.createCustomer")+'</span>');
			r.append(d);
			t.append(r);
			r.on('mouseenter', rowEnter.bind(this, idx));
			r.on('mouseleave', rowLeave.bind(this, idx));
			r.on('click', rowClick.bind(this, idx));
		}

		resultsDiv.append(t);

		// set position
		this.suggestShow(ins.minWidth);
	}

	suggestShow(minWidth) {
		this.results.show();
		var defaultMinWidth = this.options.minWidth > 0 ? this.options.minWidth : minWidth;

		const widgetWidth = this.widget.width() - 10;
		$('.mailSuggRow').each(function() {
			$(this).css('width', (widgetWidth)+"px");
		});

		let suggPos = this.widget.offset();
		suggPos.top += this.widget.outerHeight()+1;
		this.results.offset(suggPos);
		let w;
		if (defaultMinWidth > 0) w = Math.max(this.widget.outerWidth(), defaultMinWidth);
		else w = this.widget.outerWidth();
		this.results.width(w);
	}

	searchFinally(data) {
		const siq = this.searchInQueue;
		this.justSearching = null;
		this.searchInQueue = null;
		if (siq !== null && typeof siq !== 'undefined') this.queryServerWith(siq);
	}

	get queryWord() {
		let expanded = this.widget.attr(EXPAND_QUERY_ATTR);
		if (expanded) return expanded;
		let word = this.widget.val().trim();
		if (typeof this.queryParser === 'function') word = this.queryParser(this.options, word);
		return word;
	}
}



/**
 * Suggester module
 */
export const Suggester = (function($){
	const	suggests =  new Map();	// Holds all suggesters on the page
	// TODO isn't this a potential memory leak????

	function sinfo(qid) {
		return suggests.get(qid);
	}

	function defaultQueryParser(options, q) {
		if (options.separator === null || options.separator.length === 0) return q.trim();
		return q.substr(q.lastIndexOf(options.separator)+1, q.length).trim();
	}

	var pub = {};
	pub.initSuggester = function(o) {
		var instance = suggests.get(o.queryId);
		if (!instance) {
            // console.log("creating new suggest object for id:"+o.pkId+" and cached to array:"+suggests.size);
			// Create new
			const queryInput = $('#' + o.queryId);
			const pg = new StandardPropagator($('#'+o.pkId), queryInput, o.propagator);
			instance = new Suggest(queryInput, $('#'+o.queryId+"_results"), o.suggester, defaultQueryParser, pg, {
				diffTitle : (o.queryId !== o.pkId),
				separator: o.separator,
				suggEmpty: o.suggEmpty,
				suggNew: o.suggNew,
				stayFocused: o.stayFocused,
				minWidth: o.minWidth,
				formColor: o.formColor,
				addEntity: o.addEntity,
				chars: o.suggEmpty ? 0 : 2
			}, o.options);

			// instance.addEntity = o.addEntity;
			// instance.add = instance.addEntity!==null && instance.addEntity.length>0;
			// instance.onnew = null;		// called when user types a new value

			suggests.set(o.queryId,instance);
		} else {
			instance.serverOptions = o.options;	// update options
		}
		return instance;
	}
	pub.find = sinfo;
	return pub;
})(jQuery);

/*
 * queryId - id of the element holding user-visible query
 * pkId    - id of the element containing real value sent to server
 * suggester - name of the suggester implementation on the server
 * options - to server
 * separator - char to separate multiple values in query
 * propagator - JS function used to propagate values from selected item
 * suggEmpty - should we contact server to offer suggestions on empty query field?
 */
window.initSuggest = function(queryId, pkId, suggester, options, separator,
					 propagator, suggEmpty, stayFocused, minWidth, addEntity, formColor)
{
	let o = {};
	o.queryId = queryId;
	o.pkId = pkId;
	o.suggester = suggester;
	o.options = options;
	o.separator = separator;
	o.propagator = propagator;
	o.suggEmpty = suggEmpty;
	o.stayFocused = stayFocused;
	o.minWidth = minWidth;
	o.addEntity = addEntity;
	o.formColor = formColor;
	return Suggester.initSuggester(o);
};


// **************** DEFAULT PROPAGATORS ***************

window.entityPropagator = function(data) {};

window.personPropagator = function (data) {
	if (data.id.person) {
		if (data.id.formId) id(data.id.formId + ".person").value = data.id.person;
		else id("person").value = data.id.person;
	} else {
		if (data.id.formId) id(data.id.formId + ".person").value = "0";
		else id("person").value = "0";
	}
	branchPropagator(data);
};

window.branchPropagator = function (data) {
	if (data.id.branch) {
		if (data.id.formId) id(data.id.formId + ".branch").value = data.id.branch;
		else id("branch").value = data.id.branch;
	}
	else {
		if (data.id.formId) id(data.id.formId + ".branch").value = "0";
		else id("branch").value = "0";
	}
};

window.addCustomerPropagator = function (data) {
	if (data.id.branch) {
		if (data.id.formId) id(data.id.formId + ".addBranch").value = data.id.branch;
		else id("addBranch").value = data.id.branch;
	}
	else {
		if (data.id.formId) id(data.id.formId + ".addBranch").value = "0";
		else id("addBranch").value = "0";
	}
};

window.customerPropagator = function (data) {
	if (data.id.customerFileAs) {
		if (data.id.formId) id(data.id.formId + ".customerFileAs_PK_").value = data.id.customerFileAs;
		else id("customerFileAs_PK_").value = data.id.customerFileAs;

		if (data.id.formId) id(data.id.formId + ".customerFileAs").value = data.title.customerFileAs;
		else id("customerFileAs").value = data.title.customerFileAs;
	}

	if (data.id.person) personPropagator(data);
	if (data.id.branch) branchPropagator(data);
};

window.mailPropagator = function (data) {
	const customerFileAs_PK = $("#customerFileAs_PK_");
	const softCustomerPair = $("#softCustomerPair");

	if ((customerFileAs_PK.length > 0 && (customerFileAs_PK.val() === "0" || customerFileAs_PK.val() === ""))
		|| (softCustomerPair.length > 0 && softCustomerPair.val() === "1")) {
		customerPropagator(data);
		if (softCustomerPair && customerFileAs_PK.val() > 0) softCustomerPair.val("0");
	}
};

window.offerPropagator = function (data) {
	if (data.id.opportunity) {
		if (data.id.formId) id(data.id.formId + ".opportunityName_PK_").value = data.id.opportunity;
		else id("opportunityName_PK_").value = data.id.opportunity;

		if (data.id.formId) id(data.id.formId + ".opportunityName").value = data.title.opportunity;
		else id("opportunityName").value = data.title.opportunity;
	}
	customerPropagator(data);
};

window.buscasePropagator = function (data) {
	offerPropagator(data);
};

window.opportunityPropagator = function (data) {
	offerPropagator(data);
};

window.offerPropagatorWS = function (data) {
	if (data?.id?.opportunity) id("opportunityFileAs_PK_").value = data.id.opportunity;
	if (data?.title?.opportunity) id("opportunityFileAs").value = data.title.opportunity;
	customerPropagator(data);
};

window.buscasePropagatorWS = function (data) {
	offerPropagatorWS(data);
};

window.eventPropagatorWS = function (data) {
	if (data?.id?.buscase) id("buscaseFileAs_PK_").value = data.id.buscase;
	if (data?.title?.buscase) id("buscaseFileAs").value = data.title.buscase;
	offerPropagatorWS(data);
};

window.taskPropagatorWS = function (data) {
	eventPropagatorWS(data);
};

window.problemPropagatorWS = function (data) {
	customerPropagator(data);
};

window.cityPropagator = function (data) {
	if (data.id.post) id("city").value = data.id.post;
};

window.personFilterPropagator = function (data) {
	if (data.id.person) {
		id("valueStr_PK_").value = data.id.person;
	}
	else {
		id("valueStr_PK_").value = "0";
	}
};

window.acceptorPropagator = function (data) {

	if (typeof data.id.person !== 'undefined') id("acceptorPerson").value = data.id.person;
	else id("acceptorPerson").value = "0";
	if (typeof data.id.branch !== 'undefined') id("acceptorBranch").value = data.id.branch;
	else id("acceptorBranch").value = "0";
};

window.entityPersonPropagator = function (data) {
	if (data.id.person) {
		if (data.id.formId) id(data.id.formId + ".entityPerson").value = data.id.person;
		else id("entityPerson").value = data.id.person;
	}
	else {
		if (data.id.formId) id(data.id.formId + ".entityPerson").value = "0";
		else id("entityPerson").value = "0";
	}
	entityBranchPropagator(data);
};

window.entityBranchPropagator = function (data) {
	if (data.id.branch) {
		if (data.id.formId) id(data.id.formId + ".entityBranch").value = data.id.branch;
		else id("entityBranch").value = data.id.branch;
	}
	else {
		if (data.id.formId) id(data.id.formId + ".entityBranch").value = "0";
		else id("entityBranch").value = "0";
	}
};

/**
 * Universal function for extracting context params for suggester
 * @param {string} extraParams - static params as a string key1=val1,key2=val2,...
 * @return {string} extracted parameters
 */
window.extractContext = function (extraParams) {
	const ctx = [];
	$('input[data-ctx]').each(function() {
		const ctxattr = $(this).attr("data-ctx");
		let name;
		if (ctxattr.length === 0 || ctxattr === 'data-ctx' || ctxattr === 'true') name = $(this).attr("name");
		else name = ctxattr;
		let val = $(this).val();
		if (val.length > 0 && val != "0") ctx.push(name + "=" + val);
	});
	if (ctx.length === 0) return extraParams;
	let ctxStr = ctx.join(",");
	if (extraParams) ctxStr = ctxStr + "," + extraParams;
	//console.log("Suggest.js - extracted context: " + ctxStr);
	return ctxStr;
};
